import { Alert } from '@creditornot/cb-alert';
import { Spinner } from '@creditornot/cb-progress';
import { Modal, ModalContent, ModalHeader } from '@creditornot/cbt-modal';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { fetchPurchase } from 'apis/merchant-gateway/merchant-analytics/api';
import { CreditAcquisition } from 'apis/restaurant-api/merchant-admin/credits/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import CompensationOfferBanner from 'modules/insights-v2/components/ratings/CompensationOffers/CompensationOfferBanner';

import PurchaseDetailsContent from './PurchaseDetailsContent';

type PurchaseDetailsPanelProps = {
  purchaseId: string;
  showFees: boolean;
  venue: Venue;
  onClose: () => void;
  sentOffer?: CreditAcquisition;
  onSendOffer?: () => void;
  orderComment?: string;
};

const SpinnerContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  padding-block-start: 32px;
`;

const PurchaseDetailsPanel = ({
  purchaseId,
  showFees,
  venue,
  onClose,
  sentOffer,
  onSendOffer,
}: PurchaseDetailsPanelProps) => {
  const {
    data: purchase,
    isError,
    isLoading,
  } = useQuery(
    [`/merchant-analytics-api/v2/venues/${venue.id}/purchases/${purchaseId}`],
    () => fetchPurchase({ venueId: venue.id, purchaseId }),
    { refetchOnWindowFocus: false, retry: false },
  );

  return (
    <Modal
      onDismiss={onClose}
      disableContentPadding
      open
      animateOnMount
      sizePreset={{
        desktop: 'sideEndSheet:medium',
        mobile: 'fullScreen',
      }}
    >
      <ModalHeader onCloseClick={onClose}>
        <FormattedMessage id="purchases.details-heading" />
      </ModalHeader>

      <ModalContent>
        {isLoading ? (
          <SpinnerContainer>
            <Spinner size="large" />
          </SpinnerContainer>
        ) : isError ? (
          <Alert variant="error">
            <FormattedMessage id="errors.unknown-error-retry-contact" />
          </Alert>
        ) : (
          !!purchase && (
            <PurchaseDetailsContent
              purchase={purchase}
              timezone={venue.timezone}
              showFees={showFees}
              showTaxID={venue.country === 'POL'}
              compensationOffer={sentOffer}
              compensationOfferBanner={
                !!onSendOffer && (
                  <CompensationOfferBanner
                    venueId={venue.id}
                    currency={venue.currency}
                    purchaseId={purchaseId}
                    rating={purchase.review?.rating ?? undefined}
                    onSendOffer={onSendOffer}
                  />
                )
              }
            />
          )
        )}
      </ModalContent>
    </Modal>
  );
};

export default PurchaseDetailsPanel;
